/**
 * 体験の人の許可の表。列は「見る」と「使う」の 2 つ (2026-10-15 代表「体験の人には色々見せて OK。ただ受講生のみの鍵の演出にする」)。
 *
 * 見る   画面を開く (loader)。/luagate の下は全部見せる。人の名前や面談の記録が並ぶ画面だけは閉じる (TRIAL_HIDDEN_PATHS)
 * 使う   送信する (action)、レッスンを開いて進める、コードを動かす、動画を再生する、予約する。
 *        体験の 30 日、実践プロジェクト 0 と 1、道場、クラウドエディターなど、前から体験に開いていたものだけ (TRIAL_USE_PATHS)
 *
 * 画面は「使う」が false の物に錠と「受講生のみ」の札を付ける (LgMembersOnly)。サーバーの入場の判定も同じ表を見る。
 * 住所は大文字、余分な / 、エンコードをそのまま受け、表と一字一致しなければ閉じる
 *
 * 実践 PJ の中身を開く範囲 (下の canUse の practical-projects の 0 と 1、章は TRIAL_MAX_CHAPTER_NUM まで) は api-go にも同じ表がある。
 * apps/api-go/internal/domain/practicalchapter/trial_scope.go (/graphql の章の本文を断る、TrialMaxChapterNum) と
 * apps/api-go/graph/trial_write_guard.go の trialPracticalProjectIDs も同じ commit で直す
 */
/** 体験の人が「使える」住所 (送信も通す)。前から体験に開いていたもの */
const TRIAL_USE_PATHS = new Set([
  "/luagate",
  "/luagate/trial",
  // Dashboard loads recent community posts through this authenticated
  // resource route after first paint. Trial users can open the dashboard, so
  // the companion read-only request must be allowed as well.
  "/luagate/dashboard-tail",
  "/luagate/vocab-check",
  "/luagate/confirmation-test",
  "/luagate/practical-projects",
  "/luagate/team-projects",
  "/luagate/playground/code",
  // 2026-10-09 に消した Linux ターミナルの旧 URL。学習パスの本文やブックマークに残っているので、
  // 体験の人も /luagate へ戻されず、クラウドエディターの転送まで届くようにする
  "/luagate/playground/linux",
  "/luagate/textbook",
  // クラウドエディターは入口 (サイドバーの cloudeditor) を体験にも出している。開けるかは api-go の判定で、
  // 入れない人にはページが準備中の画面を出す (段階 members+trial で期限内の体験だけが開ける)
  "/luagate/cloudeditor",
  // 練習道場の一覧は体験の人にも見せる。道場のコースは /learn/courses/dojo-* にあり、この許可リストの外で元から開く
  "/luagate/dojo",
  // IT単語チェックは練習道場の 1 つ。古い /luagate/vocab-check も、送りが通るよう残す
  "/luagate/dojo/vocab-check",
  // 前のロードマップ。学習パスの地図へ送るだけ
  "/luagate/roadmap",
  "/luagate/roadmap/lessons",
  // ダッシュボードのスキルマップの帯が読む口。中身を返すかは skill-map-audience が決める
  "/luagate/roadmap/skillmap",
  // 投稿用の素材。読むだけの頁で、絵の書き出しも画面の中だけ。拡散が目的なので体験の人にも使ってもらう
  "/luagate/share-kit",
]);

/** 体験の人に見せない画面。ほかの受講生の名前、面談の記録、紹介の報酬が並ぶ */
const TRIAL_HIDDEN_PATHS = [
  "/luagate/mentoring",
  "/luagate/referral",
  "/luagate/admin",
  // 面談と振り返りへ寄せた古い住所 (merged-pages)。送った先が見られないので、ここも閉じる
  "/luagate/goal-sheet",
  "/luagate/monthly-report",
  "/luagate/early-member",
];

/**
 * 画面の「使う」は true でも、体験の人には送らせない操作 (action の _action の値)。
 * 一覧の画面は体験にも開くが、参加の応募だけは受講生のみ (画面の「参加応募は正式受講者のみ」と同じ)
 */
const TRIAL_MEMBERS_ONLY_ACTIONS: Record<string, readonly string[]> = {
  "/luagate/practical-projects": ["joinProject"],
};

/** 体験の人が読める実践 PJ の最大の章番号。api-go の practicalchapter.TrialMaxChapterNum と同じ値にする */
export const TRIAL_MAX_CHAPTER_NUM = 7;

export type TrialAccess = { view: boolean; use: boolean };

function normalize(pathname: string): string {
  const routePath = pathname.replace(/\.data$/, "");
  return routePath.length > 1 && routePath.endsWith("/")
    ? routePath.slice(0, -1)
    : routePath;
}

function decodePath(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

function canUse(path: string): boolean {
  if (TRIAL_USE_PATHS.has(path)) return true;
  if (path.startsWith("/luagate/trial/")) return true;
  // 実践PJは TODO (0、お手本コードを含む) と EC サイト (1) の中身まで開放。章は TRIAL_MAX_CHAPTER_NUM まで
  // (api-go の trial_scope.go の TrialMaxChapterNum と同じ。8 章より後は /graphql が本文を断る)
  const pj = /^\/luagate\/practical-projects\/[01](?:\/([^/]*))?(?:\/|$)/.exec(path);
  if (pj) {
    // 章の番号はエンコード (%38 など) を解いてから見る。書き方で 8 章より後へ抜けさせない
    const chapter = pj[1] === undefined ? undefined : decodePath(pj[1]);
    // 開くのは PJ の表紙、お手本コード (code)、10 進の数字だけの章。+8、0x8、8e0 のような書き方は
    // loader と api-go が Number() で 8 章と読んで閉じるので、ここでも使えない扱いにして錠を付ける
    if (chapter === undefined || chapter === "code") return true;
    if (!/^\d+$/.test(chapter)) return false;
    return Number(chapter) <= TRIAL_MAX_CHAPTER_NUM;
  }
  return false;
}

/** 体験の人がこの住所を見られるか、使えるか。使えるものは必ず見られる */
export function trialAccess(pathname: string): TrialAccess {
  const path = normalize(pathname);
  const inLuagate = path === "/luagate" || path.startsWith("/luagate/");
  // 見せない画面の判定は、ルーターと同じく大文字小文字を問わず、エンコードと余分な / を解いてから見る (書き方で抜けさせない)
  const probe = decodePath(path).toLowerCase().replace(/\/{2,}/g, "/").replace(/\.data$/, "").replace(/\/$/, "");
  const hidden = TRIAL_HIDDEN_PATHS.some((h) => probe === h || probe.startsWith(`${h}/`) || probe.startsWith(`${h}.`));
  const view = inLuagate && !hidden;
  return { view, use: view && canUse(path) };
}

/**
 * 住所を持たない物 (画面の中に並ぶ受講生の実データ、授業の参加の口) は、それを本来使う画面の行で決める。
 * 物のための表を別に持たない (許可は 1 か所の表のまま)。loader は「使う」が false の物を返さない
 */
export const LUAGATE_ITEM_PATH = {
  /** 授業の Zoom、録画、アンケート、資料の URL。使う画面は授業アーカイブ (体験は「見る」だけ) */
  lectureLinks: "/luagate/videos",
  /** ほかの受講生の名前、ユーザー ID、貢献メモ、日報の全文 (Mattermost から)。面談と振り返りの画面と同じ行 (体験には見せない。名簿の画面は 2026-10-16 に消した) */
  peerProfiles: "/luagate/mentoring",
  /** 月間ランキング (ほかの受講生の名前と順位)。月報の画面と同じ行 */
  monthlyRanking: "/luagate/monthly-report",
} as const;

/**
 * 見ている人の「見る」「使う」。受講生と管理者は全部 true、体験の人は表で決める。
 * loader と画面は role を自分で見ず、ここを通す (体験かどうかの印と、サイトの管理者かの印だけを渡す)。
 * サイトの管理者 (users.role が admin) は、チームの役割が trial でも全部通す (章の本文を直す人が本文を見られなくならないように)
 */
export function viewerAccess(
  isTrial: boolean,
  pathname: string,
  { isSiteAdmin = false }: { isSiteAdmin?: boolean } = {},
): TrialAccess {
  if (isSiteAdmin) return { view: true, use: true };
  return isTrial ? trialAccess(pathname) : { view: true, use: true };
}

/**
 * 体験の人がこの URL を開けるか。読み込み (loader) は「見る」、送信 (action) は write を true にして「使う」で決める
 */
export function isTrialAllowedPath(
  pathname: string,
  { write = false }: { write?: boolean } = {},
): boolean {
  const access = trialAccess(pathname);
  return write ? access.use : access.view;
}

/**
 * 体験の人がこの操作を送れるか。画面ごとの「使う」に加え、操作の名前で受講生のみにした物を弾く。
 * 住所の書き方は trialAccess と同じ規則で揃える
 */
export function isTrialAllowedAction(pathname: string, action: string | null | undefined): boolean {
  if (!trialAccess(pathname).use) return false;
  const key = decodePath(normalize(pathname)).toLowerCase().replace(/\/{2,}/g, "/").replace(/\/$/, "");
  return !(TRIAL_MEMBERS_ONLY_ACTIONS[key] ?? []).includes(action ?? "");
}
